/**
 * Local storage for annotations, so notes can be written, edited and deleted
 * with no connection and pushed to the server later.
 *
 * Two things live here, in one object store:
 *
 * - A **mirror** of the annotations this device knows about, so a chapter's
 *   notes are readable (and editable) offline.
 * - The **sync state** of each of those rows: what still needs pushing, and
 *   which server version the local copy was derived from.
 *
 * Keeping both on one row, rather than a mirror plus a separate outbox, is
 * deliberate. IndexedDB cannot index `null`, so rows with `pendingOp: null`
 * drop out of the `pending` index on their own — the index *is* the queue,
 * with nothing to keep in step with the mirror. It also means repeated offline
 * edits to one note simply overwrite each other (there is never a stack of
 * deltas to replay), and a note created and then deleted while offline can be
 * dropped outright because its row proves it never reached the server.
 *
 * A second store records which chapters have been listed from the server. That
 * is what separates "this chapter has no notes" from "we have never fetched
 * this chapter" — without it, an offline visit is indistinguishable from an
 * empty chapter, which is the bug that makes the reader show "You have no
 * annotations" and keep showing it.
 *
 * This uses its own database rather than adding stores to `seed-bible-offline`
 * (see `OfflineTranslationStore`). Bumping that database's version would make
 * a tab still holding the old connection fire `onblocked`, which its open path
 * rejects on — breaking offline *translations* in that tab. A second database
 * costs nothing and cannot regress a shipped feature.
 *
 * Everything goes through the {@link OfflineAnnotationStore} interface so
 * callers never touch IndexedDB directly, which is also what lets tests swap in
 * {@link createInMemoryAnnotationStore}.
 */

import type { Annotation } from "./AnnotationsManager";
import { requestToPromise, transactionToPromise } from "./indexedDbUtils";

export const ANNOTATION_DB_NAME = "seed-bible-annotations";
export const ANNOTATION_DB_VERSION = 1;

const ANNOTATIONS_STORE = "annotations";
const CHAPTERS_STORE = "chapters";
const CHAPTER_INDEX = "chapter";
const PENDING_INDEX = "pending";

/**
 * The owner used for notes written while signed out.
 *
 * Not a real account id, so it can never collide with one: CasualOS user ids
 * are UUIDs. Rows under this owner are never pushed to the server — there is no
 * record to push them to — and are re-keyed to the real account by
 * {@link OfflineAnnotationStore.adoptLocalRows} on first sign-in.
 */
export const LOCAL_OWNER = "local";

/** What a row still needs pushed to the server. */
export type PendingOp = "upsert" | "delete";

/**
 * How many times a push is retried against a server that keeps reporting a
 * retryable failure before the row is left alone and surfaced as an error.
 * Without a cap, a row the server will never accept is retried on every
 * reconnect forever.
 */
export const MAX_SYNC_ATTEMPTS = 5;

/** One annotation as this device knows it, plus what the server still needs. */
export interface StoredAnnotation {
  /** `${owner}/${annotationId}`. */
  key: string;

  /** The account this belongs to, or {@link LOCAL_OWNER} while signed out. */
  owner: string;

  /** The annotation's id, which is also the CasualOS address it lives at. */
  annotationId: string;

  /** The book the annotation targets. Duplicated out of `annotation` so a tombstone still has it. */
  bookId: string;

  /** The chapter the annotation targets. Duplicated for the same reason. */
  chapterNumber: number;

  /** The local truth. Null once this row is a tombstone. */
  annotation: Annotation | null;

  /**
   * True when the annotation was deleted locally. The row outlives the
   * annotation because the deletion itself may still need pushing, and because
   * erasing a CasualOS record leaves nothing behind to distinguish "deleted"
   * from "never existed".
   */
  deleted: boolean;

  /** When the local change was made. The tombstone's time when `deleted`. */
  updatedAtMs: number;

  /**
   * `data.updatedAtMs` of the server version this local state was derived from,
   * or null when it has never existed on the server.
   *
   * This is the conflict-detection base pointer. Note what it is *not*: a
   * comparison of "whose timestamp is larger". Timestamps here are generated by
   * whichever device made the edit, so clock skew between two devices makes
   * "newer" unreliable. Asking "does the server still hold what I edited?" does
   * not depend on the clocks agreeing.
   */
  baseUpdatedAtMs: number | null;

  /**
   * Content hash of that same server version.
   *
   * `updatedAtMs` is optional on an annotation, so records written before it
   * existed — and any written through the raw `saveAnnotation` path — carry no
   * timestamp at all. For those, comparing content is the only way to tell
   * "unchanged" from "changed".
   */
  baseFingerprint: string | null;

  /** What still needs pushing. Null once the server matches this row. */
  pendingOp: PendingOp | null;

  /** Failed push attempts, so a row the server keeps rejecting stops retrying. */
  attempts: number;
}

/** Records that a chapter's full annotation list has been read from the server. */
export interface StoredAnnotationChapter {
  /** `${owner}/${bookId}/${chapterNumber}`. */
  key: string;
  owner: string;
  bookId: string;
  chapterNumber: number;
  /** When the list was last read. */
  listedAtMs: number;
}

export interface OfflineAnnotationStore {
  /** Every row for a chapter, tombstones included, so callers can filter them. */
  listForChapter(
    owner: string,
    bookId: string,
    chapterNumber: number
  ): Promise<StoredAnnotation[]>;

  /** One row, or null if this device has never seen that annotation. */
  get(owner: string, annotationId: string): Promise<StoredAnnotation | null>;

  /** Writes a row, replacing any existing one. */
  put(record: StoredAnnotation): Promise<void>;

  /** Removes a row entirely — not a tombstone, no trace left. */
  delete(owner: string, annotationId: string): Promise<void>;

  /** Every row still waiting to be pushed, oldest change first. */
  listPending(owner: string): Promise<StoredAnnotation[]>;

  /** The chapter's list metadata, or null when it has never been listed. */
  getChapter(
    owner: string,
    bookId: string,
    chapterNumber: number
  ): Promise<StoredAnnotationChapter | null>;

  /**
   * Folds a server list into the mirror and marks the chapter as listed.
   *
   * Pending rows are left exactly as they are: they hold changes the server has
   * not seen, so the server's copy is not newer information about them, and
   * deciding between the two is the sync pass's job. Everything else is brought
   * in line with the server, including dropping rows the server no longer has
   * (deleted on another device).
   */
  reconcileChapter(
    owner: string,
    bookId: string,
    chapterNumber: number,
    serverAnnotations: Annotation[],
    listedAtMs: number
  ): Promise<void>;

  /**
   * Re-keys every {@link LOCAL_OWNER} row to `owner` and returns them.
   *
   * Used when someone who wrote notes while signed out signs in: the notes
   * become that account's. The move and the clear happen in one transaction so
   * the notes cannot be adopted twice — otherwise a second account signing in
   * on the same (possibly shared) device would inherit them, which is the leak
   * `LoginManager` already guards against for anonymous settings.
   */
  adoptLocalRows(owner: string): Promise<StoredAnnotation[]>;

  /**
   * Drops an owner's fully-synced rows, keeping anything still pending.
   *
   * Called on sign-out. Synced notes can be fetched again, and leaving readable
   * prose on a shared device is the thing clearing the profile cache exists to
   * prevent — but deleting writing that has never reached the server would be
   * data loss, so those rows stay.
   */
  clearSynced(owner: string): Promise<void>;
}

function annotationKey(owner: string, annotationId: string): string {
  return `${owner}/${annotationId}`;
}

function chapterKey(
  owner: string,
  bookId: string,
  chapterNumber: number
): string {
  return `${owner}/${bookId}/${chapterNumber}`;
}

/**
 * Serializes a value with object keys in sorted order, so two structurally
 * equal annotations always produce the same string.
 *
 * `JSON.stringify` preserves insertion order, which zod's parse does not
 * guarantee to match between a locally-built annotation and one read back from
 * the server. Sorting removes that as a source of false "changed" verdicts.
 */
function canonicalize(value: unknown): string {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value) ?? "null";
  }
  if (Array.isArray(value)) {
    return `[${value.map(canonicalize).join(",")}]`;
  }
  const entries = Object.entries(value as Record<string, unknown>)
    // `undefined` and a missing key mean the same thing for an optional field,
    // so dropping them keeps the two spellings from hashing differently.
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return `{${entries
    .map(([k, v]) => `${JSON.stringify(k)}:${canonicalize(v)}`)
    .join(",")}}`;
}

/**
 * A stable fingerprint of an annotation's content.
 *
 * Deliberately excludes `data.updatedAtMs`: this is used to answer "is the
 * server's copy still the one I edited?" for records that have no usable
 * timestamp, so mixing the timestamp back in would defeat the purpose.
 */
export function annotationFingerprint(annotation: Annotation): string {
  const { updatedAtMs: _updatedAtMs, ...data } = annotation.data;
  return canonicalize({ ...annotation, data });
}

/** Reads the annotation's own last-modified time, if it has one. */
export function annotationUpdatedAtMs(annotation: Annotation): number | null {
  return typeof annotation.data.updatedAtMs === "number"
    ? annotation.data.updatedAtMs
    : null;
}

/**
 * Builds the row for an annotation that matches the server exactly.
 *
 * Used both when a push succeeds and when a server list is folded in, so the
 * base pointers are always set the same way in both places.
 */
export function syncedRow(
  owner: string,
  annotation: Annotation
): StoredAnnotation {
  return {
    key: annotationKey(owner, annotation.id),
    owner,
    annotationId: annotation.id,
    bookId: annotation.bookId,
    chapterNumber: annotation.chapterNumber,
    annotation,
    deleted: false,
    updatedAtMs: annotationUpdatedAtMs(annotation) ?? 0,
    baseUpdatedAtMs: annotationUpdatedAtMs(annotation),
    baseFingerprint: annotationFingerprint(annotation),
    pendingOp: null,
    attempts: 0,
  };
}

/**
 * Creates the IndexedDB-backed store.
 *
 * Returns null when IndexedDB is unavailable — during server-side rendering,
 * and in browsers that block storage (private windows in some browsers, or a
 * sandboxed iframe). Callers treat null as "this device can't hold notes
 * locally" and fall back to talking to the server directly.
 */
export function createIndexedDbAnnotationStore(): OfflineAnnotationStore | null {
  if (typeof indexedDB === "undefined") {
    return null;
  }

  let databasePromise: Promise<IDBDatabase> | null = null;

  const openDatabase = (): Promise<IDBDatabase> => {
    if (databasePromise) {
      return databasePromise;
    }

    databasePromise = new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(ANNOTATION_DB_NAME, ANNOTATION_DB_VERSION);

      request.onupgradeneeded = () => {
        const database = request.result;
        if (!database.objectStoreNames.contains(ANNOTATIONS_STORE)) {
          const annotations = database.createObjectStore(ANNOTATIONS_STORE, {
            keyPath: "key",
          });
          annotations.createIndex(CHAPTER_INDEX, [
            "owner",
            "bookId",
            "chapterNumber",
          ]);
          // IndexedDB skips records whose indexed value is null, so this index
          // contains exactly the rows that still need pushing.
          annotations.createIndex(PENDING_INDEX, ["owner", "pendingOp"]);
        }
        if (!database.objectStoreNames.contains(CHAPTERS_STORE)) {
          database.createObjectStore(CHAPTERS_STORE, { keyPath: "key" });
        }
      };

      request.onsuccess = () => {
        const database = request.result;
        // A version change from another tab invalidates this handle; drop the
        // cached promise so the next call reopens instead of using a dead one.
        database.onversionchange = () => {
          database.close();
          databasePromise = null;
        };
        resolve(database);
      };

      request.onerror = () =>
        reject(request.error ?? new Error("Failed to open IndexedDB."));
      request.onblocked = () =>
        reject(new Error("IndexedDB upgrade blocked by another tab."));
    }).catch((error: unknown) => {
      databasePromise = null;
      throw error;
    });

    return databasePromise;
  };

  const listForChapter = async (
    owner: string,
    bookId: string,
    chapterNumber: number
  ): Promise<StoredAnnotation[]> => {
    const database = await openDatabase();
    const transaction = database.transaction(ANNOTATIONS_STORE, "readonly");
    const rows = await requestToPromise(
      transaction
        .objectStore(ANNOTATIONS_STORE)
        .index(CHAPTER_INDEX)
        .getAll(IDBKeyRange.only([owner, bookId, chapterNumber]))
    );
    return rows as StoredAnnotation[];
  };

  const get = async (
    owner: string,
    annotationId: string
  ): Promise<StoredAnnotation | null> => {
    const database = await openDatabase();
    const transaction = database.transaction(ANNOTATIONS_STORE, "readonly");
    const row = await requestToPromise(
      transaction
        .objectStore(ANNOTATIONS_STORE)
        .get(annotationKey(owner, annotationId))
    );
    return (row as StoredAnnotation | undefined) ?? null;
  };

  const put = async (record: StoredAnnotation): Promise<void> => {
    const database = await openDatabase();
    const transaction = database.transaction(ANNOTATIONS_STORE, "readwrite");
    transaction.objectStore(ANNOTATIONS_STORE).put(record);
    await transactionToPromise(transaction);
  };

  const deleteRow = async (
    owner: string,
    annotationId: string
  ): Promise<void> => {
    const database = await openDatabase();
    const transaction = database.transaction(ANNOTATIONS_STORE, "readwrite");
    transaction
      .objectStore(ANNOTATIONS_STORE)
      .delete(annotationKey(owner, annotationId));
    await transactionToPromise(transaction);
  };

  const listPending = async (owner: string): Promise<StoredAnnotation[]> => {
    const database = await openDatabase();
    const transaction = database.transaction(ANNOTATIONS_STORE, "readonly");
    const store = transaction
      .objectStore(ANNOTATIONS_STORE)
      .index(PENDING_INDEX);
    const [upserts, deletes] = await Promise.all([
      requestToPromise(store.getAll(IDBKeyRange.only([owner, "upsert"]))),
      requestToPromise(store.getAll(IDBKeyRange.only([owner, "delete"]))),
    ]);
    return sortPending([
      ...(upserts as StoredAnnotation[]),
      ...(deletes as StoredAnnotation[]),
    ]);
  };

  const getChapter = async (
    owner: string,
    bookId: string,
    chapterNumber: number
  ): Promise<StoredAnnotationChapter | null> => {
    const database = await openDatabase();
    const transaction = database.transaction(CHAPTERS_STORE, "readonly");
    const row = await requestToPromise(
      transaction
        .objectStore(CHAPTERS_STORE)
        .get(chapterKey(owner, bookId, chapterNumber))
    );
    return (row as StoredAnnotationChapter | undefined) ?? null;
  };

  const reconcileChapter = async (
    owner: string,
    bookId: string,
    chapterNumber: number,
    serverAnnotations: Annotation[],
    listedAtMs: number
  ): Promise<void> => {
    const database = await openDatabase();
    const transaction = database.transaction(
      [ANNOTATIONS_STORE, CHAPTERS_STORE],
      "readwrite"
    );
    const store = transaction.objectStore(ANNOTATIONS_STORE);

    // Read inside the same read-write transaction as the writes, not before it.
    // Reading separately left a gap the event loop could fill with a local edit,
    // and the decision about what to overwrite is made from `existing` — so an
    // edit landing in that gap looked unpending and got replaced by the server's
    // older copy, silently dropping it. A transaction stays alive across the
    // await of its own request, so this is still one atomic unit.
    const existing = (await requestToPromise(
      store
        .index(CHAPTER_INDEX)
        .getAll(IDBKeyRange.only([owner, bookId, chapterNumber]))
    )) as StoredAnnotation[];

    applyReconcile(existing, serverAnnotations, owner, {
      put: (row) => void store.put(row),
      delete: (key) => void store.delete(key),
    });

    transaction.objectStore(CHAPTERS_STORE).put({
      key: chapterKey(owner, bookId, chapterNumber),
      owner,
      bookId,
      chapterNumber,
      listedAtMs,
    } satisfies StoredAnnotationChapter);

    await transactionToPromise(transaction);
  };

  const adoptLocalRows = async (owner: string): Promise<StoredAnnotation[]> => {
    const database = await openDatabase();
    const transaction = database.transaction(ANNOTATIONS_STORE, "readwrite");
    const store = transaction.objectStore(ANNOTATIONS_STORE);
    const rows = (await requestToPromise(store.getAll())) as StoredAnnotation[];
    const { adopt, discard } = partitionLocalRows(rows);

    const adopted = adopt.map((row) => adoptRow(row, owner));
    for (const row of discard) {
      store.delete(row.key);
    }
    for (const row of adopted) {
      store.put(row);
    }

    await transactionToPromise(transaction);
    return adopted;
  };

  const clearSynced = async (owner: string): Promise<void> => {
    const database = await openDatabase();
    const transaction = database.transaction(ANNOTATIONS_STORE, "readwrite");
    const store = transaction.objectStore(ANNOTATIONS_STORE);
    const rows = (await requestToPromise(store.getAll())) as StoredAnnotation[];
    for (const row of rows) {
      if (row.owner === owner && row.pendingOp === null) {
        store.delete(row.key);
      }
    }
    await transactionToPromise(transaction);
  };

  return {
    listForChapter,
    get,
    put,
    delete: deleteRow,
    listPending,
    getChapter,
    reconcileChapter,
    adoptLocalRows,
    clearSynced,
  };
}

/** Oldest local change first, so a burst of offline edits pushes in order. */
function sortPending(rows: StoredAnnotation[]): StoredAnnotation[] {
  return [...rows].sort((a, b) => a.updatedAtMs - b.updatedAtMs);
}

/**
 * Moves a signed-out row onto a real account.
 *
 * The base pointers are cleared because the annotation has never been on any
 * server, so the sync pass must treat it as a fresh create rather than as an
 * update to something it could compare against.
 */
function adoptRow(row: StoredAnnotation, owner: string): StoredAnnotation {
  return {
    ...row,
    key: annotationKey(owner, row.annotationId),
    owner,
    baseUpdatedAtMs: null,
    baseFingerprint: null,
    pendingOp: "upsert",
    attempts: 0,
  };
}

/**
 * Splits signed-out rows into the ones worth adopting and the ones to discard.
 *
 * A tombstone written while signed out refers to a note that only ever existed
 * on this device, so there is nothing on the server to delete and nothing to
 * adopt — carrying it onto the account would just leave a permanent dead row.
 */
function partitionLocalRows(rows: StoredAnnotation[]): {
  adopt: StoredAnnotation[];
  discard: StoredAnnotation[];
} {
  const local = rows.filter((row) => row.owner === LOCAL_OWNER);
  return {
    adopt: local.filter((row) => !row.deleted),
    discard: local,
  };
}

/**
 * Works out the row changes that bring a chapter in line with a server list,
 * and hands them to a writer.
 *
 * Split out from the transaction so both store implementations share one
 * definition of what reconciliation means.
 */
function applyReconcile(
  existing: StoredAnnotation[],
  serverAnnotations: Annotation[],
  owner: string,
  writer: {
    put: (row: StoredAnnotation) => void;
    delete: (key: string) => void;
  }
): void {
  const byId = new Map(existing.map((row) => [row.annotationId, row]));
  const serverIds = new Set(serverAnnotations.map((a) => a.id));

  for (const annotation of serverAnnotations) {
    const row = byId.get(annotation.id);
    // A pending row holds a change the server hasn't seen. The server's copy
    // isn't newer information about it, and choosing between them is the sync
    // pass's job, so leave it untouched.
    if (row?.pendingOp) {
      continue;
    }
    writer.put(syncedRow(owner, annotation));
  }

  for (const row of existing) {
    if (serverIds.has(row.annotationId) || row.pendingOp) {
      continue;
    }
    // Synced locally but gone from the server: deleted on another device.
    writer.delete(row.key);
  }
}

/**
 * An in-memory store with the same semantics as the IndexedDB one.
 *
 * Used by tests (jsdom has no IndexedDB) and usable as a fallback anywhere
 * persistence isn't available but the code paths still need to work.
 */
export function createInMemoryAnnotationStore(): OfflineAnnotationStore {
  const rows = new Map<string, StoredAnnotation>();
  const chapters = new Map<string, StoredAnnotationChapter>();

  /**
   * Synchronous so {@link reconcileChapter} can read and write without yielding.
   *
   * That matters for more than tidiness: this store stands in for the IndexedDB
   * one in tests, so it has to share its atomicity. Awaiting between the read and
   * the writes would reintroduce the very gap a concurrent local edit used to
   * fall into and get overwritten in.
   */
  const rowsForChapter = (
    owner: string,
    bookId: string,
    chapterNumber: number
  ): StoredAnnotation[] =>
    [...rows.values()].filter(
      (row) =>
        row.owner === owner &&
        row.bookId === bookId &&
        row.chapterNumber === chapterNumber
    );

  const listForChapter = async (
    owner: string,
    bookId: string,
    chapterNumber: number
  ): Promise<StoredAnnotation[]> =>
    rowsForChapter(owner, bookId, chapterNumber);

  return {
    listForChapter,

    async get(owner, annotationId) {
      return rows.get(annotationKey(owner, annotationId)) ?? null;
    },

    async put(record) {
      rows.set(record.key, record);
    },

    async delete(owner, annotationId) {
      rows.delete(annotationKey(owner, annotationId));
    },

    async listPending(owner) {
      return sortPending(
        [...rows.values()].filter(
          (row) => row.owner === owner && row.pendingOp !== null
        )
      );
    },

    async getChapter(owner, bookId, chapterNumber) {
      return chapters.get(chapterKey(owner, bookId, chapterNumber)) ?? null;
    },

    async reconcileChapter(
      owner,
      bookId,
      chapterNumber,
      serverAnnotations,
      listedAtMs
    ) {
      const existing = rowsForChapter(owner, bookId, chapterNumber);
      applyReconcile(existing, serverAnnotations, owner, {
        put: (row) => rows.set(row.key, row),
        delete: (key) => rows.delete(key),
      });
      chapters.set(chapterKey(owner, bookId, chapterNumber), {
        key: chapterKey(owner, bookId, chapterNumber),
        owner,
        bookId,
        chapterNumber,
        listedAtMs,
      });
    },

    async adoptLocalRows(owner) {
      const { adopt, discard } = partitionLocalRows([...rows.values()]);
      const adopted = adopt.map((row) => adoptRow(row, owner));
      for (const row of discard) {
        rows.delete(row.key);
      }
      for (const row of adopted) {
        rows.set(row.key, row);
      }
      return adopted;
    },

    async clearSynced(owner) {
      for (const row of [...rows.values()]) {
        if (row.owner === owner && row.pendingOp === null) {
          rows.delete(row.key);
        }
      }
    },
  };
}
